/* oxlint-disable next/no-img-element -- The small brand SVG is already an optimized static asset. */
import {
  AlertTriangle,
  Archive,
  ArrowRight,
  Check,
  Download,
  FileImage,
  ImagePlus,
  LoaderCircle,
  LockKeyhole,
  RotateCcw,
  Share2,
  ShieldCheck,
  Smartphone,
  Trash2,
  WandSparkles,
  X,
} from 'lucide-react';
import {
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { zip } from 'fflate';
import {
  type ConversionAction,
  type ConversionItem,
  conversionReducer,
  summarizeItems,
} from './lib/conversion-state';
import { loadIdeaIdentity, sendAppIdea } from './lib/idea-api';
import { trackAnalytics } from './lib/analytics';
import {
  chooseFilesForBatch,
  formatBytes,
  outputName,
  uniqueArchiveNames,
} from './lib/files';
import {
  MAX_BATCH_BYTES,
  MAX_FILE_BYTES,
  MAX_FILES,
  validateHeicFile,
} from './lib/heif';
import type {
  JpegQuality,
  WorkerConvertRequest,
  WorkerResponse,
} from './lib/worker-protocol';
import { registerConverterTools, type ConverterStatus } from './webmcp';

const QUALITY_OPTIONS: Array<{
  value: JpegQuality;
  label: string;
  detail: string;
}> = [
  { value: 75, label: 'Smaller', detail: '75%' },
  { value: 90, label: 'Balanced', detail: '90%' },
  { value: 95, label: 'Best', detail: '95%' },
];

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function createId() {
  return crypto.randomUUID();
}

function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

function makeZip(entries: Record<string, Uint8Array>): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    zip(entries, { level: 0 }, (error, archive) => {
      if (error) reject(error);
      else resolve(archive);
    });
  });
}

function App() {
  const [items, reactDispatch] = useReducer(conversionReducer, []);
  const itemsRef = useRef<ConversionItem[]>([]);
  const [quality, setQualityState] = useState<JpegQuality>(90);
  const qualityRef = useRef<JpegQuality>(90);
  const [running, setRunningState] = useState(false);
  const runningRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const [message, setMessage] = useState('');
  const [zipBusy, setZipBusy] = useState(false);
  const [canShare] = useState(() => typeof navigator.share === 'function');
  const [installPrompt, setInstallPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [installOpen, setInstallOpen] = useState(false);
  const [installed, setInstalled] = useState(() => (
    window.matchMedia('(display-mode: standalone)').matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
  ));
  const workerRef = useRef<Worker | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    trackAnalytics('page_view', 'heic-to-jpg');
  }, []);

  const commit = useCallback((action: ConversionAction) => {
    itemsRef.current = conversionReducer(itemsRef.current, action);
    reactDispatch(action);
  }, []);

  const setQuality = useCallback((next: JpegQuality) => {
    qualityRef.current = next;
    setQualityState(next);
  }, []);

  const setRunning = useCallback((next: boolean) => {
    runningRef.current = next;
    setRunningState(next);
  }, []);

  useEffect(() => {
    return () => {
      workerRef.current?.terminate();
      for (const item of itemsRef.current) {
        if (item.outputUrl) URL.revokeObjectURL(item.outputUrl);
      }
    };
  }, []);

  useEffect(() => {
    const onPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallOpen(false);
      setInstallPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!installPrompt) return;
    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      setInstallPrompt(null);
      if (choice.outcome === 'accepted') setInstallOpen(false);
    } catch {
      setInstallPrompt(null);
    }
  };

  const summary = useMemo(() => summarizeItems(items), [items]);
  const totalBytes = useMemo(
    () => items.reduce((total, item) => total + item.size, 0),
    [items],
  );

  const getStatus = useCallback((): ConverterStatus => {
    const latest = summarizeItems(itemsRef.current);
    return {
      quality: qualityRef.current,
      selected: itemsRef.current.length,
      queued: latest.queued,
      converting: latest.converting,
      ready: latest.ready,
      errors: latest.error,
      running: runningRef.current,
    };
  }, []);

  const getWorker = useCallback(() => {
    if (!workerRef.current) {
      workerRef.current = new Worker(
        new URL('./converter.worker.ts', import.meta.url),
        { type: 'module', name: 'bucksimple-heic-converter' },
      );
    }
    return workerRef.current;
  }, []);

  const resetWorker = useCallback(() => {
    workerRef.current?.terminate();
    workerRef.current = null;
  }, []);

  const convertOne = useCallback(
    async (item: ConversionItem, selectedQuality: JpegQuality) => {
      const buffer = await item.file.arrayBuffer();
      const worker = getWorker();

      return new Promise<Extract<WorkerResponse, { type: 'success' }>>(
        (resolve, reject) => {
          const cleanup = () => {
            window.clearTimeout(timeout);
            worker.removeEventListener('message', onMessage);
            worker.removeEventListener('error', onWorkerError);
            worker.removeEventListener('messageerror', onMessageError);
          };
          const onMessage = (event: MessageEvent<WorkerResponse>) => {
            const response = event.data;
            if (response.type === 'ready' || response.id !== item.id) return;
            cleanup();
            if (response.type === 'error') reject(new Error(response.error));
            else resolve(response);
          };
          const onWorkerError = () => {
            cleanup();
            resetWorker();
            reject(
              new Error(
                'The converter stopped unexpectedly. Please try again.',
              ),
            );
          };
          const onMessageError = () => {
            cleanup();
            resetWorker();
            reject(
              new Error('The browser could not read the converted photo.'),
            );
          };
          const timeout = window.setTimeout(() => {
            cleanup();
            resetWorker();
            reject(
              new Error(
                'This photo took too long to convert. Please try it again.',
              ),
            );
          }, 120_000);

          worker.addEventListener('message', onMessage);
          worker.addEventListener('error', onWorkerError);
          worker.addEventListener('messageerror', onMessageError);
          const request: WorkerConvertRequest = {
            type: 'convert',
            id: item.id,
            buffer,
            quality: selectedQuality,
          };
          worker.postMessage(request, [buffer]);
        },
      );
    },
    [getWorker, resetWorker],
  );

  const startConversion = useCallback(async (): Promise<ConverterStatus> => {
    if (runningRef.current) {
      throw new Error('A conversion is already running.');
    }

    const queue = itemsRef.current.filter((item) => item.status === 'queued');
    if (queue.length === 0) {
      throw new Error('Select at least one valid HEIC photo first.');
    }
    let convertedThisRun = 0;
    trackAnalytics('tool_attempt', 'heic-to-jpg');

    setRunning(true);
    setMessage(
      `Converting ${queue.length} ${queue.length === 1 ? 'photo' : 'photos'}…`,
    );
    const selectedQuality = qualityRef.current;

    try {
      for (const item of queue) {
        commit({
          type: 'patch',
          id: item.id,
          patch: { status: 'converting', error: undefined },
        });

        try {
          const result = await convertOne(item, selectedQuality);
          const outputBlob = new Blob([result.buffer], { type: 'image/jpeg' });
          const outputUrl = URL.createObjectURL(outputBlob);
          commit({
            type: 'patch',
            id: item.id,
            patch: {
              status: 'ready',
              outputBlob,
              outputUrl,
              outputName: outputName(item.name),
              outputSize: outputBlob.size,
              width: result.width,
              height: result.height,
              qualityUsed: selectedQuality,
              error: undefined,
              retryable: false,
            },
          });
          convertedThisRun += 1;
        } catch (error) {
          commit({
            type: 'patch',
            id: item.id,
            patch: {
              status: 'error',
              error:
                error instanceof Error
                  ? error.message
                  : 'This photo could not be converted.',
              retryable: true,
            },
          });
        }
      }
    } finally {
      setRunning(false);
    }

    const latest = getStatus();
    if (convertedThisRun > 0) trackAnalytics('tool_success', 'heic-to-jpg');
    setMessage(
      latest.ready > 0
        ? `${latest.ready} ${latest.ready === 1 ? 'JPG is' : 'JPGs are'} ready.`
        : 'No photos were converted. Review the errors below.',
    );
    return latest;
  }, [commit, convertOne, getStatus, setRunning]);

  const startRef = useRef(startConversion);
  const statusRef = useRef(getStatus);

  useEffect(() => {
    startRef.current = startConversion;
    statusRef.current = getStatus;
  }, [getStatus, startConversion]);

  useEffect(
    () =>
      registerConverterTools({
        getStatus: () => statusRef.current(),
        convertSelected: () => startRef.current(),
      }),
    [],
  );

  const addFiles = useCallback(
    async (incoming: File[]) => {
      if (runningRef.current || incoming.length === 0) return;

      const current = itemsRef.current;
      const currentBytes = current.reduce(
        (total, item) => total + item.size,
        0,
      );
      const choice = chooseFilesForBatch(
        current.length,
        currentBytes,
        incoming,
      );
      if (choice.reason) setMessage(choice.reason);
      else setMessage('');

      if (choice.accepted.length === 0) return;

      const additions: ConversionItem[] = choice.accepted.map((file) => ({
        id: createId(),
        file,
        name: file.name,
        size: file.size,
        status: 'checking',
      }));
      commit({ type: 'add', items: additions });

      for (const item of additions) {
        const validation = await validateHeicFile(item.file);
        commit({
          type: 'patch',
          id: item.id,
          patch: validation.ok
            ? {
                status: 'queued',
                width: validation.dimensions?.width,
                height: validation.dimensions?.height,
              }
            : {
                status: 'error',
                error: validation.error,
                width: validation.dimensions?.width,
                height: validation.dimensions?.height,
                retryable: false,
              },
        });
      }
    },
    [commit],
  );

  const onFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    void addFiles(Array.from(event.target.files ?? []));
    event.target.value = '';
  };

  const onDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDragging(false);
    void addFiles(Array.from(event.dataTransfer.files));
  };

  const removeItem = (item: ConversionItem) => {
    if (runningRef.current) return;
    if (item.outputUrl) URL.revokeObjectURL(item.outputUrl);
    commit({ type: 'remove', id: item.id });
  };

  const clearAll = () => {
    if (runningRef.current) return;
    for (const item of itemsRef.current) {
      if (item.outputUrl) URL.revokeObjectURL(item.outputUrl);
    }
    resetWorker();
    commit({ type: 'clear' });
    setMessage('');
  };

  const retryItem = (item: ConversionItem) => {
    if (runningRef.current || !item.retryable) return;
    commit({
      type: 'patch',
      id: item.id,
      patch: { status: 'queued', error: undefined, retryable: false },
    });
    setMessage('Photo queued to try again.');
  };

  const shareItem = async (item: ConversionItem) => {
    if (!item.outputBlob || !item.outputName) return;
    const file = new File([item.outputBlob], item.outputName, {
      type: 'image/jpeg',
    });
    if (!navigator.canShare?.({ files: [file] })) {
      saveBlob(item.outputBlob, item.outputName);
      return;
    }
    try {
      await navigator.share({ files: [file], title: item.outputName });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setMessage('Sharing was not available, so use Download instead.');
    }
  };

  const downloadZip = async () => {
    const ready = itemsRef.current.filter(
      (item) => item.status === 'ready' && item.outputBlob && item.outputName,
    );
    if (ready.length < 2) return;

    setZipBusy(true);
    setMessage('Packing your JPGs…');
    try {
      const names = uniqueArchiveNames(ready.map((item) => item.outputName!));
      const entries: Record<string, Uint8Array> = {};
      for (const [index, item] of ready.entries()) {
        entries[names[index]] = new Uint8Array(
          await item.outputBlob!.arrayBuffer(),
        );
      }
      const archive = await makeZip(entries);
      const archiveBuffer = new Uint8Array(archive).buffer;
      saveBlob(
        new Blob([archiveBuffer], { type: 'application/zip' }),
        'bucksimple-jpgs.zip',
      );
      setMessage('ZIP is ready.');
    } catch {
      setMessage(
        'The ZIP could not be made. Your individual JPGs are still ready.',
      );
    } finally {
      setZipBusy(false);
    }
  };

  const processed = summary.ready + summary.error;
  const activeTotal = processed + summary.queued + summary.converting;

  return (
    <div className="site-frame">
      <header className="topbar">
        <a
          className="brand"
          href="https://bucksimple.com/"
          aria-label="BuckSimple home"
        >
          <img className="brand-logo" src="/favicon.svg" alt="" width={40} height={40} />
          <span className="brand-wordmark">BuckSimple</span>
        </a>
        <div className="header-actions">
          <span className="tool-name">HEIC → JPG</span>
          {!installed && (
            <button
              className="install-trigger"
              type="button"
              onClick={() => setInstallOpen(true)}
            >
              <Smartphone size={15} aria-hidden="true" />
              <span>Add to phone</span>
            </button>
          )}
        </div>
      </header>

      <main>
        <section className="intro" aria-labelledby="page-title">
          <div className="eyebrow">
            <span /> PHOTO FORMAT TOOL
          </div>
          <h1 id="page-title">HEIC to JPG Converter</h1>
          <p className="lede">
            Pick your iPhone photos, choose the quality, and download clean
            JPGs. Everything happens right here in your browser.
          </p>
          <div className="privacy-chip">
            <LockKeyhole size={15} aria-hidden="true" />
            BETA · Photos stay on this device
          </div>
        </section>

        <section className="converter" aria-label="HEIC to JPG converter">
          <div className="converter-topline">
            <span>01</span>
            <p>ADD PHOTOS</p>
            <span className="line" />
          </div>

          <input
            ref={fileInputRef}
            className="file-input"
            id="heic-files"
            type="file"
            accept=".heic,.heif,image/heic,image/heif"
            multiple
            disabled={running || items.length >= MAX_FILES}
            onChange={onFileInput}
          />
          <button
            type="button"
            className={`dropzone${dragging ? ' is-dragging' : ''}${running ? ' is-disabled' : ''}`}
            disabled={running || items.length >= MAX_FILES}
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={(event) => {
              event.preventDefault();
              if (!running) setDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => {
              if (
                !event.currentTarget.contains(
                  event.relatedTarget as Node | null,
                )
              ) {
                setDragging(false);
              }
            }}
            onDrop={onDrop}
          >
            <span className="drop-icon">
              <ImagePlus size={28} aria-hidden="true" />
            </span>
            <strong>
              {items.length >= MAX_FILES
                ? '12-photo limit reached'
                : 'Choose HEIC photos'}
            </strong>
            <span className="drop-copy">or drop them here</span>
            <span className="drop-limits">
              Up to {MAX_FILES} files · {formatBytes(MAX_FILE_BYTES)} each ·{' '}
              {formatBytes(MAX_BATCH_BYTES)} total
            </span>
          </button>

          <div className="settings-row">
            <div className="setting-heading">
              <span>02</span>
              <div>
                <strong>JPG quality</strong>
                <small>Balanced works for most photos.</small>
              </div>
            </div>
            <fieldset className="quality-control" disabled={running}>
              <legend className="sr-only">JPG quality</legend>
              {QUALITY_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={quality === option.value ? 'is-selected' : ''}
                >
                  <input
                    type="radio"
                    name="quality"
                    value={option.value}
                    checked={quality === option.value}
                    onChange={() => setQuality(option.value)}
                  />
                  <span>{option.label}</span>
                  <small>{option.detail}</small>
                </label>
              ))}
            </fieldset>
          </div>

          {items.length > 0 && (
            <div className="file-area">
              <div className="file-summary">
                <div>
                  <strong>
                    {items.length} {items.length === 1 ? 'photo' : 'photos'}
                  </strong>
                  <span>{formatBytes(totalBytes)} selected</span>
                </div>
                {!running && (
                  <button
                    className="text-button"
                    type="button"
                    onClick={clearAll}
                  >
                    <Trash2 size={16} aria-hidden="true" /> Clear all
                  </button>
                )}
              </div>

              {running && activeTotal > 0 && (
                <div
                  className="overall-progress"
                  aria-label={`${processed} of ${activeTotal} processed`}
                >
                  <span
                    style={{
                      width: `${Math.round((processed / activeTotal) * 100)}%`,
                    }}
                  />
                </div>
              )}

              <ul className="file-list" aria-label="Selected photos">
                {items.map((item) => (
                  <FileRow
                    key={item.id}
                    item={item}
                    running={running}
                    canShare={canShare}
                    onRemove={() => removeItem(item)}
                    onRetry={() => retryItem(item)}
                    onShare={() => void shareItem(item)}
                  />
                ))}
              </ul>
            </div>
          )}

          <div className="action-row">
            <button
              className="convert-button"
              type="button"
              disabled={running || summary.checking > 0 || summary.queued === 0}
              onClick={() => {
                void startConversion().catch((error: unknown) => {
                  setMessage(
                    error instanceof Error
                      ? error.message
                      : 'Conversion could not start.',
                  );
                });
              }}
            >
              {running ? (
                <>
                  <LoaderCircle className="spin" size={20} aria-hidden="true" />{' '}
                  Converting one at a time
                </>
              ) : (
                <>
                  <WandSparkles size={20} aria-hidden="true" /> Convert{' '}
                  {summary.queued || 'selected'}{' '}
                  {summary.queued === 1 ? 'photo' : 'photos'}{' '}
                  <ArrowRight size={19} aria-hidden="true" />
                </>
              )}
            </button>
            {summary.ready > 1 && (
              <button
                className="zip-button"
                type="button"
                disabled={zipBusy || running}
                onClick={() => void downloadZip()}
              >
                {zipBusy ? (
                  <LoaderCircle className="spin" size={18} />
                ) : (
                  <Archive size={18} />
                )}
                Download all as ZIP
              </button>
            )}
          </div>

          <output className="live-message" aria-live="polite">
            {message}
          </output>
        </section>

        <section className="simple-proof" aria-label="Private and simple">
          <ShieldCheck size={19} aria-hidden="true" />
          <p>
            <strong>No uploads. No account.</strong> Your photos stay on this
            device, and their metadata is left behind.
          </p>
        </section>

        <section className="details-grid">
          <article className="limitations">
            <p className="section-kicker">GOOD TO KNOW</p>
            <h2>One photo in. One clean JPG out.</h2>
            <p>
              BuckSimple converts the primary still image only. The motion clip
              from a Live Photo is not included. HDR photos become standard
              JPGs, so very bright colors may look a little different.
              Transparency is placed on white, and location, camera, and other
              embedded metadata are not copied.
            </p>
          </article>

          <div className="faq" aria-label="Common questions">
            <details>
              <summary>What are HEIC and HEIF files?</summary>
              <p>
                They are space-saving photo formats commonly used by iPhones.
                JPG is easier to open, upload, and share almost anywhere.
              </p>
            </details>
            <details>
              <summary>Are my photos uploaded?</summary>
              <p>
                No. Conversion runs inside your browser. BuckSimple does not
                send the photos or their filenames to a server.
              </p>
            </details>
            <details>
              <summary>Why can a conversion fail?</summary>
              <p>
                A photo may be damaged, encrypted, over the size limits, or use
                a HEIC variant the browser decoder does not support.
              </p>
            </details>
          </div>
        </section>

        <SuggestionForm />
      </main>

      <footer>
        <a className="brand footer-brand" href="https://bucksimple.com/" aria-label="BuckSimple home">
          <img className="brand-logo" src="/favicon.svg" alt="" width={40} height={40} />
          <span className="brand-wordmark">BuckSimple</span>
        </a>
        <p>One small job. One simple app.</p>
        <nav aria-label="Footer">
          <a href="https://bucksimple.com/">All tools</a>
          <a href="https://bucksimple.com/privacy/">Privacy</a>
          <a href="https://bucksimple.com/terms/">Terms</a>
          <a href="/notices.html">Licenses &amp; notices</a>
        </nav>
      </footer>
      <InstallDialog
        open={installOpen}
        promptAvailable={Boolean(installPrompt)}
        onInstall={() => void promptInstall()}
        onClose={() => setInstallOpen(false)}
      />
    </div>
  );
}

function InstallDialog({
  open,
  promptAvailable,
  onInstall,
  onClose,
}: {
  open: boolean;
  promptAvailable: boolean;
  onInstall: () => void;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const userAgent = navigator.userAgent.toLocaleLowerCase();
  const isIos =
    /iphone|ipad|ipod/.test(userAgent) ||
    (/macintosh/.test(userAgent) && navigator.maxTouchPoints > 1);
  const isAndroid = /android/.test(userAgent);
  const isSafari =
    /safari/.test(userAgent) &&
    !/(crios|fxios|edgios|chrome|chromium)/.test(userAgent);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="install-dialog"
      aria-labelledby="install-title"
      onCancel={onClose}
    >
      <div className="dialog-card">
        <button
          className="dialog-close"
          type="button"
          onClick={onClose}
          aria-label="Close install help"
        >
          <X size={20} aria-hidden="true" />
        </button>
        <span className="dialog-icon">
          <Smartphone size={25} aria-hidden="true" />
        </span>
        <p className="section-kicker">KEEP IT HANDY</p>
        <h2 id="install-title">Add BuckSimple to your home screen</h2>
        {promptAvailable ? (
          <>
            <p>
              Install this tool for one-tap access. It comes straight from this
              website—not an app store.
            </p>
            <button
              className="dialog-primary"
              type="button"
              onClick={onInstall}
            >
              Install now
            </button>
          </>
        ) : isIos ? (
          <>
            <p>
              {isSafari
                ? 'In Safari, tap the Share button, scroll down, then choose Add to Home Screen.'
                : 'Open this page in Safari. Tap Share, scroll down, then choose Add to Home Screen.'}
            </p>
            <button className="dialog-primary" type="button" onClick={onClose}>
              Got it
            </button>
          </>
        ) : isAndroid ? (
          <>
            <p>
              In Chrome, open the three-dot menu and choose Install app or Add
              to Home screen.
            </p>
            <button className="dialog-primary" type="button" onClick={onClose}>
              Got it
            </button>
          </>
        ) : (
          <>
            <p>
              Look for an install icon in your browser’s address bar or an
              Install app option in its menu. If it is not offered, bookmark
              this page instead.
            </p>
            <button className="dialog-primary" type="button" onClick={onClose}>
              Got it
            </button>
          </>
        )}
      </div>
    </dialog>
  );
}

function SuggestionForm() {
  const [idea, setIdea] = useState('');
  const [email, setEmail] = useState('');
  const [emailFromDropbox, setEmailFromDropbox] = useState(false);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState('Sent here in BuckSimple — no email app opens.');

  useEffect(() => {
    void loadIdeaIdentity().then((identity) => {
      if (!identity.email) return;
      setEmail(identity.email);
      setEmailFromDropbox(true);
    }).catch(() => undefined);
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = idea.trim();
    if (trimmed.length < 3) {
      setFeedback('Tell us a little more about the job you want to simplify.');
      return;
    }
    if (!email.trim()) {
      setFeedback('Enter your email so Rob can reply.');
      return;
    }
    setSending(true);
    setFeedback('Sending…');
    try {
      await sendAppIdea({ idea: trimmed, replyEmail: email });
      setIdea('');
      setFeedback('Thanks — Rob has your idea.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Your idea could not be sent. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="suggestion" aria-labelledby="suggestion-title">
      <div>
        <p className="section-kicker">WANT ANOTHER SIMPLE APP?</p>
        <h2 id="suggestion-title">Tell us the one little job.</h2>
      </div>
      <form onSubmit={submit}>
        <label className="suggestion-email" htmlFor="app-idea-email">Your email
          <input id="app-idea-email" type="email" maxLength={254} required readOnly={emailFromDropbox} value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" autoComplete="email" />
          {emailFromDropbox ? <span>Email filled automatically</span> : null}
        </label>
        <label htmlFor="app-idea" className="sr-only">
          Your simple app idea
        </label>
        <input
          id="app-idea"
          value={idea}
          maxLength={180}
          onChange={(event) => setIdea(event.target.value)}
          placeholder="Example: rename a batch of photos"
        />
        <button type="submit" disabled={sending}>
          {sending ? 'Sending…' : 'Send idea'} <ArrowRight size={16} aria-hidden="true" />
        </button>
        <small>Sent here in BuckSimple.</small>
        <output className="suggestion-feedback" aria-live="polite">
          {feedback}
        </output>
      </form>
    </section>
  );
}

function FileRow({
  item,
  running,
  canShare,
  onRemove,
  onRetry,
  onShare,
}: {
  item: ConversionItem;
  running: boolean;
  canShare: boolean;
  onRemove: () => void;
  onRetry: () => void;
  onShare: () => void;
}) {
  const statusLabel = {
    checking: 'Checking',
    queued: 'Ready to convert',
    converting: 'Converting',
    ready: 'JPG ready',
    error: 'Needs attention',
  }[item.status];

  return (
    <li className={`file-row status-${item.status}`}>
      <div className="file-preview" aria-hidden="true">
        {item.outputUrl ? (
          <img src={item.outputUrl} alt="" />
        ) : (
          <FileImage size={22} />
        )}
        {item.status === 'ready' && (
          <span>
            <Check size={12} />
          </span>
        )}
      </div>
      <div className="file-info">
        <strong title={item.name}>{item.name}</strong>
        <span>
          {formatBytes(item.size)}
          {item.width && item.height ? ` · ${item.width} × ${item.height}` : ''}
          {item.outputSize ? ` → ${formatBytes(item.outputSize)}` : ''}
        </span>
        {item.error && (
          <small>
            <AlertTriangle size={13} aria-hidden="true" /> {item.error}
          </small>
        )}
      </div>
      <div className="file-status">
        <span className="status-label">
          {item.status === 'checking' || item.status === 'converting' ? (
            <LoaderCircle className="spin" size={14} aria-hidden="true" />
          ) : item.status === 'ready' ? (
            <Check size={14} aria-hidden="true" />
          ) : item.status === 'error' ? (
            <AlertTriangle size={14} aria-hidden="true" />
          ) : null}
          {statusLabel}
        </span>
        {item.status === 'ready' && item.outputBlob && item.outputName && (
          <div className="file-actions">
            <button
              type="button"
              onClick={() => saveBlob(item.outputBlob!, item.outputName!)}
            >
              <Download size={16} aria-hidden="true" /> Download
            </button>
            {canShare && (
              <button type="button" onClick={onShare}>
                <Share2 size={16} aria-hidden="true" /> Share
              </button>
            )}
          </div>
        )}
        {item.status === 'error' && item.retryable && !running && (
          <button className="retry-button" type="button" onClick={onRetry}>
            <RotateCcw size={15} aria-hidden="true" /> Try again
          </button>
        )}
      </div>
      {!running && item.status !== 'converting' && (
        <button
          className="remove-button"
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${item.name}`}
        >
          <X size={18} aria-hidden="true" />
        </button>
      )}
    </li>
  );
}

export default App;
